import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useContact, useContactMutations, useActivityMutations } from '../hooks/useCRM';
import ActivityTimeline from '../components/ActivityTimeline';
import ContactForm from '../components/ContactForm';
import FilesPanel from '../components/FilesPanel';
import { useToast } from '../components/Toast';
import { useConfirm } from '../hooks/useConfirm';
import '../crm.css';
import { contactsPath, dealPath } from '../utils/crmEnv';

// Label maps
const typeLabels = {
    customer: 'Customer',
    prospect: 'Prospect',
    partner: 'Partner',
    vendor: 'Vendor',
    other: 'Other'
};

// Date formatter
const formatDate = (dateString, includeTime = false) => {
    if (!dateString) return '-';
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: includeTime ? '2-digit' : undefined,
            minute: includeTime ? '2-digit' : undefined
        });
    } catch {
        return dateString;
    }
};

const ContactDetailPage = () => {
    const { id } = useParams();
    const history = useHistory();

    // Hooks
    const { contact, loading, error, refetch } = useContact(id);
    const { update, remove, loading: mutationLoading } = useContactMutations();
    const { create: createActivity } = useActivityMutations();
    const { showSuccess, showError } = useToast();
    const { confirm, ConfirmDialog } = useConfirm();

    // State
    const [showEditModal, setShowEditModal] = useState(false);
    const [showActivityModal, setShowActivityModal] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    const [savingNotes, setSavingNotes] = useState(false);
    const [notesDraft, setNotesDraft] = useState('');
    const [requirementsDraft, setRequirementsDraft] = useState(null);
    const [ratingDraft, setRatingDraft] = useState(0);
    const [matchingTab, setMatchingTab] = useState('summary');

    const [newActivity, setNewActivity] = useState({
        type: 'call',
        title: '',
        description: '',
        dueDate: ''
    });

    // Handlers
    const handleDelete = async () => {
        const isConfirmed = await confirm({
            title: 'Delete Contact',
            message: 'Are you sure you want to delete this contact? This action cannot be undone.',
            confirmText: 'Delete Contact',
            variant: 'danger'
        });

        if (isConfirmed) {
            try {
                await remove(id);
                showSuccess('Contact deleted successfully');
                history.push(contactsPath());
            } catch (err) {
                console.error('Failed to delete contact:', err);
                showError('Failed to delete contact');
            }
        }
    };

    const handleEditSubmit = async (formData) => {
        try {
            await update(id, formData);
            showSuccess('Contact updated successfully');
            setShowEditModal(false);
            refetch();
        } catch (err) {
            console.error('Failed to update contact:', err);
            showError('Failed to update contact');
        }
    };

    const handleActivitySubmit = async () => {
        if (!newActivity.title) return;

        try {
            await createActivity({
                ...newActivity,
                entityType: 'contact',
                entityId: id
            });
            showSuccess('Activity created successfully');
            setShowActivityModal(false);
            setNewActivity({ type: 'call', title: '', description: '', dueDate: '' });
            refetch();
        } catch (err) {
            console.error('Failed to create activity:', err);
            showError('Failed to log activity');
        }
    };

    const handleActivityComplete = async () => {
        refetch();
    };

    const handleQuickFollowUp = async (offsetDays) => {
        const next = new Date();
        next.setDate(next.getDate() + offsetDays);
        next.setHours(9, 0, 0, 0);
        try {
            await update(id, { nextFollowUpAt: next.toISOString() });
            showSuccess('Next follow-up updated');
            refetch();
        } catch (err) {
            showError('Failed to update follow-up');
        }
    };

    const handleRating = async (value) => {
        setRatingDraft(value);
        try {
            await update(id, { rating: value });
            showSuccess('Rating updated');
            refetch();
        } catch (err) {
            showError('Failed to update rating');
        }
    };

    // Helper for Initials
    const getInitials = (first, last) => {
        return `${(first || '').charAt(0)}${(last || '').charAt(0)}`.toUpperCase();
    };

    // Loading State
    if (loading) {
        return (
            <div className="crm-container">
                <div className="crm-loading">
                    <div className="crm-spinner"></div>
                </div>
            </div>
        );
    }

    // Error or Not Found State
    if (error || !contact) {
        return (
            <div className="crm-container">
                <div className="crm-empty-state">
                    <h3>{error || 'Contact not found'}</h3>
                    <p>The contact you are looking for does not exist or has been deleted.</p>
                    <button
                        className="crm-btn crm-btn-primary"
                        onClick={() => history.push(contactsPath())}
                    >
                        ← Back to Contacts
                    </button>
                </div>
            </div>
        );
    }

    const displayName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'Unnamed Contact';
    const titleCompany = [contact.title, contact.company].filter(Boolean).join(' at ');
    const customerType = contact.customerType || '';
    const lifecycleStage = contact.lifecycleStage || '';
    const nextFollowUp = contact.nextFollowUpAt ? new Date(contact.nextFollowUpAt) : null;
    const isOverdue = nextFollowUp ? nextFollowUp < new Date() : false;
    const ratingValue = contact.rating || 0;
    const requirements = contact.requirements || {};

    useEffect(() => {
        setNotesDraft(contact.notes || '');
        setRequirementsDraft({
            purpose: requirements.purpose || '',
            propertyTypes: requirements.propertyTypes || [],
            budgetMin: requirements.budgetMin || '',
            budgetMax: requirements.budgetMax || '',
            locations: requirements.locations || [],
            projects: requirements.projects || [],
            bhk: requirements.bhk || [],
            areaMin: requirements.areaMin || '',
            areaMax: requirements.areaMax || '',
            timeline: requirements.timeline || '',
            mustHaves: requirements.mustHaves || [],
            dealBreakers: requirements.dealBreakers || [],
        });
        setRatingDraft(ratingValue);
    }, [contact.id]);

    useEffect(() => {
        const timeoutId = setTimeout(async () => {
            if (notesDraft === (contact.notes || '')) return;
            setSavingNotes(true);
            try {
                await update(id, { notes: notesDraft });
                setSavingNotes(false);
            } catch (err) {
                setSavingNotes(false);
            }
        }, 800);
        return () => clearTimeout(timeoutId);
    }, [notesDraft]);

    const handleRequirementsSave = async () => {
        try {
            await update(id, { requirements: requirementsDraft });
            showSuccess('Requirements updated');
            refetch();
        } catch (err) {
            showError('Failed to update requirements');
        }
    };

    const deals = contact.deals || [];
    const activities = contact.activities || [];

    return (
        <div className="crm-container">
            {/* Header */}
            <div className="crm-page-header">
                <div>
                    <button
                        className="crm-btn crm-btn-link"
                        onClick={() => history.push(contactsPath())}
                        style={{ paddingLeft: 0, marginBottom: '8px' }}
                    >
                        ← Back to Contacts
                    </button>
                    <h1 className="crm-page-title">{displayName}</h1>
                </div>
                <div className="crm-page-actions">
                    <button
                        className="crm-btn crm-btn-secondary"
                        onClick={() => setShowEditModal(true)}
                    >
                        Edit
                    </button>
                    <button
                        className="crm-btn crm-btn-danger"
                        onClick={handleDelete}
                    >
                        Delete
                    </button>
                </div>
            </div>

            <div className="crm-contact-layout">
                {/* Left panel */}
                <div className="crm-contact-left">
                    <div className="crm-card crm-profile-card">
                        <div className="crm-card-body">
                            <div className="crm-profile-header">
                                <div className="crm-avatar">
                                    {getInitials(contact.firstName, contact.lastName)}
                                </div>
                                <div>
                                    <h2 className="crm-profile-name">{displayName}</h2>
                                    {titleCompany && <p className="crm-profile-subtitle">{titleCompany}</p>}
                                    <div className="crm-profile-badges">
                                        <span className={`crm-badge crm-badge-${contact.type || 'other'}`}>
                                            {typeLabels[contact.type] || contact.type || 'Other'}
                                        </span>
                                        {customerType && (
                                            <span className="crm-badge crm-badge-source">{customerType}</span>
                                        )}
                                        {lifecycleStage && (
                                            <span className={`crm-badge crm-badge-${lifecycleStage}`}>{lifecycleStage}</span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="crm-profile-actions">
                                <button className="crm-btn crm-btn-secondary" disabled={!contact.phone}>
                                    {contact.phone ? (
                                        <a href={`tel:${contact.phone}`} className="crm-btn-link">Call</a>
                                    ) : (
                                        'Call'
                                    )}
                                </button>
                                <button className="crm-btn crm-btn-secondary" disabled={!contact.phone}>
                                    {contact.phone ? (
                                        <a href={`https://wa.me/${contact.phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" className="crm-btn-link">
                                            WhatsApp
                                        </a>
                                    ) : (
                                        'WhatsApp'
                                    )}
                                </button>
                                <button className="crm-btn crm-btn-secondary" disabled={!contact.email}>
                                    {contact.email ? (
                                        <a href={`mailto:${contact.email}`} className="crm-btn-link">Email</a>
                                    ) : (
                                        'Email'
                                    )}
                                </button>
                            </div>

                            <div className="crm-followup-panel">
                                <div className="crm-followup-title">Next follow-up</div>
                                <div className={`crm-followup-date ${isOverdue ? 'text-danger' : ''}`}>
                                    {nextFollowUp ? nextFollowUp.toLocaleDateString() : 'Not set'}
                                </div>
                                <div className="crm-followup-actions">
                                    <button className="crm-btn crm-btn-secondary crm-btn-sm" onClick={() => handleQuickFollowUp(0)}>
                                        Later today
                                    </button>
                                    <button className="crm-btn crm-btn-secondary crm-btn-sm" onClick={() => handleQuickFollowUp(1)}>
                                        Tomorrow
                                    </button>
                                    <button className="crm-btn crm-btn-secondary crm-btn-sm" onClick={() => handleQuickFollowUp(3)}>
                                        +3 days
                                    </button>
                                </div>
                            </div>

                            <div className="crm-profile-info">
                                <div className="crm-field-group">
                                    <div className="crm-field-label">Email</div>
                                    <div className="crm-field-value">{contact.email || '-'}</div>
                                </div>
                                <div className="crm-field-group">
                                    <div className="crm-field-label">Phone</div>
                                    <div className="crm-field-value">{contact.phone || '-'}</div>
                                </div>
                                <div className="crm-field-group">
                                    <div className="crm-field-label">Company</div>
                                    <div className="crm-field-value">{contact.company || '-'}</div>
                                </div>
                                <div className="crm-field-group">
                                    <div className="crm-field-label">Location</div>
                                    <div className="crm-field-value">
                                        {[contact.city, contact.state, contact.country].filter(Boolean).join(', ') || '-'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Center panel */}
                <div className="crm-contact-center">
                    <div className="crm-card crm-card-hover">
                        <div className="crm-tab-bar">
                            {['overview', 'requirements', 'deals', 'activities', 'notes'].map((tab) => (
                                <button
                                    key={tab}
                                    className={`crm-tab ${activeTab === tab ? 'active' : ''}`}
                                    onClick={() => setActiveTab(tab)}
                                    type="button"
                                >
                                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                </button>
                            ))}
                        </div>
                        <div className="crm-card-body">
                            {activeTab === 'overview' && (
                                <div className="crm-detail-grid crm-detail-grid-compact">
                                    <div className="crm-field-group">
                                        <div className="crm-field-label">Customer type</div>
                                        <div className="crm-field-value">{customerType || '-'}</div>
                                    </div>
                                    <div className="crm-field-group">
                                        <div className="crm-field-label">Lifecycle stage</div>
                                        <div className="crm-field-value">{lifecycleStage || '-'}</div>
                                    </div>
                                    <div className="crm-field-group">
                                        <div className="crm-field-label">Preferred channel</div>
                                        <div className="crm-field-value">{contact.preferredChannel || '-'}</div>
                                    </div>
                                    <div className="crm-field-group">
                                        <div className="crm-field-label">Language</div>
                                        <div className="crm-field-value">{contact.language || '-'}</div>
                                    </div>
                                    <div className="crm-field-group">
                                        <div className="crm-field-label">Rating</div>
                                        <div className="crm-field-value">
                                            <div className="crm-rating">
                                                {[1, 2, 3, 4, 5].map((value) => (
                                                    <button
                                                        key={value}
                                                        type="button"
                                                        className={`crm-rating-star ${ratingDraft >= value ? 'active' : ''}`}
                                                        onClick={() => handleRating(value)}
                                                    >
                                                        ★
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="crm-field-group">
                                        <div className="crm-field-label">Budget</div>
                                        <div className="crm-field-value">
                                            {requirements.budgetMin || requirements.budgetMax
                                                ? `₹${requirements.budgetMin || '-'} - ₹${requirements.budgetMax || '-'}`
                                                : '-'}
                                        </div>
                                    </div>
                                    <div className="crm-field-group">
                                        <div className="crm-field-label">Next follow-up</div>
                                        <div className="crm-field-value">{nextFollowUp ? nextFollowUp.toLocaleDateString() : '-'}</div>
                                    </div>
                                    <div className="crm-field-group">
                                        <div className="crm-field-label">Last contacted</div>
                                        <div className="crm-field-value">{formatDate(contact.lastContactedAt, true)}</div>
                                    </div>
                                    <div className="crm-field-group">
                                        <div className="crm-field-label">Tags</div>
                                        <div className="crm-field-value">
                                            {contact.tags && contact.tags.length > 0 ? (
                                                <div className="crm-tag-list">
                                                    {contact.tags.map((tag) => (
                                                        <span key={tag} className="crm-badge crm-badge-source">{tag}</span>
                                                    ))}
                                                </div>
                                            ) : (
                                                '-'
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'requirements' && requirementsDraft && (
                                <div className="crm-requirements-card">
                                    <div className="crm-requirements-grid">
                                        <div className="crm-form-group">
                                            <label className="crm-label">Purpose</label>
                                            <select
                                                className="crm-select"
                                                value={requirementsDraft.purpose}
                                                onChange={(e) =>
                                                    setRequirementsDraft({ ...requirementsDraft, purpose: e.target.value })
                                                }
                                            >
                                                <option value="">Select</option>
                                                <option value="end_use">End use</option>
                                                <option value="investment">Investment</option>
                                                <option value="rental">Rental</option>
                                            </select>
                                        </div>
                                        <div className="crm-form-group">
                                            <label className="crm-label">Timeline</label>
                                            <select
                                                className="crm-select"
                                                value={requirementsDraft.timeline}
                                                onChange={(e) =>
                                                    setRequirementsDraft({ ...requirementsDraft, timeline: e.target.value })
                                                }
                                            >
                                                <option value="">Select</option>
                                                <option value="immediate">Immediate</option>
                                                <option value="1_month">1 month</option>
                                                <option value="3_months">3 months</option>
                                                <option value="6_months">6 months</option>
                                                <option value="flexible">Flexible</option>
                                            </select>
                                        </div>
                                        <div className="crm-form-group">
                                            <label className="crm-label">Budget min</label>
                                            <input
                                                type="number"
                                                className="crm-input"
                                                value={requirementsDraft.budgetMin}
                                                onChange={(e) =>
                                                    setRequirementsDraft({ ...requirementsDraft, budgetMin: e.target.value })
                                                }
                                            />
                                        </div>
                                        <div className="crm-form-group">
                                            <label className="crm-label">Budget max</label>
                                            <input
                                                type="number"
                                                className="crm-input"
                                                value={requirementsDraft.budgetMax}
                                                onChange={(e) =>
                                                    setRequirementsDraft({ ...requirementsDraft, budgetMax: e.target.value })
                                                }
                                            />
                                        </div>
                                        <div className="crm-form-group">
                                            <label className="crm-label">Locations (comma separated)</label>
                                            <input
                                                className="crm-input"
                                                value={(requirementsDraft.locations || []).join(', ')}
                                                onChange={(e) =>
                                                    setRequirementsDraft({
                                                        ...requirementsDraft,
                                                        locations: e.target.value.split(',').map((item) => item.trim()).filter(Boolean),
                                                    })
                                                }
                                            />
                                        </div>
                                        <div className="crm-form-group">
                                            <label className="crm-label">Projects (comma separated)</label>
                                            <input
                                                className="crm-input"
                                                value={(requirementsDraft.projects || []).join(', ')}
                                                onChange={(e) =>
                                                    setRequirementsDraft({
                                                        ...requirementsDraft,
                                                        projects: e.target.value.split(',').map((item) => item.trim()).filter(Boolean),
                                                    })
                                                }
                                            />
                                        </div>
                                        <div className="crm-form-group">
                                            <label className="crm-label">Property types</label>
                                            <input
                                                className="crm-input"
                                                value={(requirementsDraft.propertyTypes || []).join(', ')}
                                                onChange={(e) =>
                                                    setRequirementsDraft({
                                                        ...requirementsDraft,
                                                        propertyTypes: e.target.value.split(',').map((item) => item.trim()).filter(Boolean),
                                                    })
                                                }
                                            />
                                        </div>
                                        <div className="crm-form-group">
                                            <label className="crm-label">BHK</label>
                                            <input
                                                className="crm-input"
                                                value={(requirementsDraft.bhk || []).join(', ')}
                                                onChange={(e) =>
                                                    setRequirementsDraft({
                                                        ...requirementsDraft,
                                                        bhk: e.target.value.split(',').map((item) => item.trim()).filter(Boolean),
                                                    })
                                                }
                                            />
                                        </div>
                                        <div className="crm-form-group">
                                            <label className="crm-label">Area min</label>
                                            <input
                                                type="number"
                                                className="crm-input"
                                                value={requirementsDraft.areaMin}
                                                onChange={(e) =>
                                                    setRequirementsDraft({ ...requirementsDraft, areaMin: e.target.value })
                                                }
                                            />
                                        </div>
                                        <div className="crm-form-group">
                                            <label className="crm-label">Area max</label>
                                            <input
                                                type="number"
                                                className="crm-input"
                                                value={requirementsDraft.areaMax}
                                                onChange={(e) =>
                                                    setRequirementsDraft({ ...requirementsDraft, areaMax: e.target.value })
                                                }
                                            />
                                        </div>
                                    </div>
                                    <div className="crm-form-group">
                                        <label className="crm-label">Must haves</label>
                                        <input
                                            className="crm-input"
                                            value={(requirementsDraft.mustHaves || []).join(', ')}
                                            onChange={(e) =>
                                                setRequirementsDraft({
                                                    ...requirementsDraft,
                                                    mustHaves: e.target.value.split(',').map((item) => item.trim()).filter(Boolean),
                                                })
                                            }
                                        />
                                    </div>
                                    <div className="crm-form-group">
                                        <label className="crm-label">Deal breakers</label>
                                        <input
                                            className="crm-input"
                                            value={(requirementsDraft.dealBreakers || []).join(', ')}
                                            onChange={(e) =>
                                                setRequirementsDraft({
                                                    ...requirementsDraft,
                                                    dealBreakers: e.target.value.split(',').map((item) => item.trim()).filter(Boolean),
                                                })
                                            }
                                        />
                                    </div>
                                    <button className="crm-btn crm-btn-primary" type="button" onClick={handleRequirementsSave}>
                                        Save requirements
                                    </button>
                                </div>
                            )}

                            {activeTab === 'deals' && (
                                <div>
                                    <div className="crm-tab-actions">
                                        <button
                                            className="crm-btn crm-btn-primary crm-btn-sm"
                                            type="button"
                                            onClick={() => history.push(`/deals/new?contactId=${contact.id}&contactName=${encodeURIComponent(displayName)}`)}
                                        >
                                            + Create Deal
                                        </button>
                                    </div>
                                    {deals.length === 0 ? (
                                        <div className="crm-empty-state">
                                            <h3>No deals yet</h3>
                                            <p>Create a deal to track opportunities.</p>
                                        </div>
                                    ) : (
                                        <div className="crm-card">
                                            <div className="crm-card-body" style={{ padding: 0 }}>
                                                <table className="crm-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Title</th>
                                                            <th>Stage</th>
                                                            <th>Value</th>
                                                            <th>Updated</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {deals.map((deal) => (
                                                            <tr
                                                                key={deal.id}
                                                                onClick={() => history.push(dealPath(deal.id))}
                                                                style={{ cursor: 'pointer' }}
                                                            >
                                                                <td style={{ fontWeight: '500' }}>{deal.title}</td>
                                                                <td>{deal.stage}</td>
                                                                <td>₹{deal.value?.toLocaleString()}</td>
                                                                <td>{formatDate(deal.updatedAt)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === 'activities' && (
                                <div>
                                    <div className="crm-tab-actions">
                                        <button className="crm-btn crm-btn-secondary crm-btn-sm" type="button" onClick={() => setShowActivityModal(true)}>
                                            Log Call
                                        </button>
                                        <button className="crm-btn crm-btn-secondary crm-btn-sm" type="button" onClick={() => setShowActivityModal(true)}>
                                            Log Site Visit
                                        </button>
                                        <button className="crm-btn crm-btn-primary crm-btn-sm" type="button" onClick={() => setShowActivityModal(true)}>
                                            Add Task
                                        </button>
                                    </div>
                                    <ActivityTimeline
                                        activities={activities}
                                        onComplete={handleActivityComplete}
                                    />
                                </div>
                            )}

                            {activeTab === 'notes' && (
                                <div>
                                    <div className="crm-note-status">
                                        {savingNotes ? 'Saving...' : 'Saved'}
                                    </div>
                                    <textarea
                                        className="crm-textarea"
                                        rows={8}
                                        value={notesDraft}
                                        onChange={(e) => setNotesDraft(e.target.value)}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="crm-card" style={{ marginTop: '16px' }}>
                        <div className="crm-card-header">
                            <h2 className="crm-card-title">Files</h2>
                        </div>
                        <div className="crm-card-body">
                            <FilesPanel entityType="contact" entityId={id} />
                        </div>
                    </div>
                </div>

                {/* Right panel */}
                <div className="crm-contact-right">
                    <div className="crm-card">
                        <div className="crm-card-header">
                            <h2 className="crm-card-title">Matches</h2>
                            <button
                                className="crm-btn crm-btn-secondary crm-btn-sm"
                                type="button"
                                onClick={() => {
                                    if (contact.leadId) {
                                        history.push(`/leads/${contact.leadId}?tab=matches`);
                                    } else {
                                        showError('No matching flow available for this contact');
                                    }
                                }}
                            >
                                Open Matching
                            </button>
                        </div>
                        <div className="crm-card-body">
                            <div className="crm-match-placeholder">
                                <div className="crm-match-placeholder-title">Top match criteria</div>
                                <div className="crm-match-placeholder-body">
                                    {requirements.locations?.length ? requirements.locations.join(', ') : 'Add locations'}
                                    {requirements.projects?.length ? ` • ${requirements.projects.join(', ')}` : ''}
                                    {requirements.propertyTypes?.length ? ` • ${requirements.propertyTypes.join(', ')}` : ''}
                                </div>
                                <div className="crm-match-placeholder-title">Recommended next action</div>
                                <div className="crm-match-placeholder-body">
                                    {nextFollowUp ? `Follow up on ${nextFollowUp.toLocaleDateString()}` : 'Schedule a follow-up'}
                                </div>
                                <div className="crm-match-placeholder-title">Suggested message</div>
                                <div className="crm-match-placeholder-body">
                                    {displayName ? `Hi ${displayName.split(' ')[0]}, I have a few options that match your requirements. When can we connect?` : 'Hi, I have a few options that match your requirements.'}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Edit Modal */}
            {showEditModal && (
                <div className="crm-modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div
                        className="crm-modal"
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: 'var(--crm-bg)',
                            maxWidth: '600px',
                            width: '100%',
                            maxHeight: '90vh',
                            overflow: 'auto',
                            border: '1px solid var(--crm-gray-200)',
                            boxShadow: 'var(--crm-shadow-lg)'
                        }}
                    >
                        <div className="crm-modal-header">
                            <h2 className="crm-modal-title">Edit Contact</h2>
                            <button className="crm-modal-close" onClick={() => setShowEditModal(false)}>×</button>
                        </div>
                        <div className="crm-modal-body">
                            <ContactForm
                                contact={contact}
                                onSubmit={handleEditSubmit}
                                onCancel={() => setShowEditModal(false)}
                                loading={mutationLoading}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Activity Modal */}
            {showActivityModal && (
                <div className="crm-modal-overlay" onClick={() => setShowActivityModal(false)}>
                    <div
                        className="crm-modal"
                        onClick={e => e.stopPropagation()}
                        style={{ maxWidth: '500px', background: 'var(--crm-bg)', border: '1px solid var(--crm-gray-200)', boxShadow: 'var(--crm-shadow-lg)' }}
                    >
                        <div className="crm-modal-header">
                            <h2 className="crm-modal-title">Log Activity</h2>
                            <button className="crm-modal-close" onClick={() => setShowActivityModal(false)}>×</button>
                        </div>
                        <div className="crm-modal-body">
                            <div className="crm-form-group">
                                <label className="crm-label">Type</label>
                                <select
                                    className="crm-select"
                                    value={newActivity.type}
                                    onChange={(e) => setNewActivity({ ...newActivity, type: e.target.value })}
                                >
                                    <option value="call">Phone Call</option>
                                    <option value="email">Email</option>
                                    <option value="meeting">Meeting</option>
                                    <option value="task">Task</option>
                                    <option value="note">Note</option>
                                </select>
                            </div>

                            <div className="crm-form-group">
                                <label className="crm-label required">Title</label>
                                <input
                                    type="text"
                                    className="crm-input"
                                    placeholder="Summary of activity"
                                    value={newActivity.title}
                                    onChange={(e) => setNewActivity({ ...newActivity, title: e.target.value })}
                                />
                            </div>

                            <div className="crm-form-group">
                                <label className="crm-label">Description</label>
                                <textarea
                                    className="crm-textarea"
                                    placeholder="Details..."
                                    value={newActivity.description}
                                    onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })}
                                />
                            </div>

                            {['task', 'meeting'].includes(newActivity.type) && (
                                <div className="crm-form-group">
                                    <label className="crm-label">Due Date</label>
                                    <input
                                        type="datetime-local"
                                        className="crm-input"
                                        value={newActivity.dueDate}
                                        onChange={(e) => setNewActivity({ ...newActivity, dueDate: e.target.value })}
                                    />
                                </div>
                            )}

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
                                <button
                                    className="crm-btn crm-btn-secondary"
                                    onClick={() => setShowActivityModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="crm-btn crm-btn-primary"
                                    onClick={handleActivitySubmit}
                                    disabled={!newActivity.title}
                                >
                                    Save Activity
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <ConfirmDialog />
        </div>
    );
};

export default ContactDetailPage;
